import * as React from 'react';
import { Helmet } from 'react-helmet';
import { DefaultButton, Fabric, PrimaryButton } from '@fluentui/react';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { useBetreuer } from 'api/betreuer';
import { SelectOption } from 'formComponents/Select/types';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import useDebouncedState from 'hooks/useDebouncedState';
import { ValueType } from 'react-select';
import { Select } from 'formComponents/Select/Select';
import { Label } from '@fluentui/react/lib/Label';
import moment from 'moment-timezone';
import range from 'lodash/range';
import { formStyles } from 'styles/formStyles';
import { generateReportPrintUrl } from 'reports/utils';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import {
    BetreuerWorkMonthFilter,
    finishBetreuerWorkMonth,
    initBetreuerWorkMonth,
    useBetreuerWorkMonthList,
} from 'api/betreuerWorkMonth';
import { Arbeitszeit } from './Arbeitszeit';

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        height: 'calc(100vh - 75px)',
        display: 'grid',
        gridTemplateRows: 'max-content 1fr',
        gridRowGap: 10,
    },
    searchBar: {
        display: 'grid',
        gridTemplateColumns: '200px 160px 160px 1fr max-content max-content',
        gridColumnGap: 10,
        marginTop: 10,
    },
    actionButtons: {
        marginTop: 29,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, max-content)',
        gridColumnGap: 10,
    },
});

interface FilterValues {
    betreuer: SelectOption;
    workYear: number;
    workMonth: number;
}

const defaultFilterValues: FilterValues = {
    betreuer: { value: '', label: '' },
    workYear: moment().year(),
    workMonth: moment().month() + 1,
};

const yearOptions = range(moment().year(), moment().year() - 25).map((year) => ({
    value: year.toString(),
    label: year.toString(),
}));

const monthOptions = range(0, 12).map((month) => ({
    value: (month + 1).toString(),
    label: (month + 1).toString(),
}));

export const ArbeitszeitView = (): JSX.Element => {
    const auth = useSelector(authSelector);
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const betreuer = useBetreuer();
    const [showInit, setShowInit] = React.useState(false);
    const [showFinish, setShowFinish] = React.useState(false);

    const betreuerFilterOptions = React.useMemo<SelectOption[]>(() => {
        const options: SelectOption[] = [];

        if (auth.rolle !== 'GF') {
            options.push({
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            });
        } else {
            betreuer
                .filter((b) => b.aktiv && b.name)
                .sort((a, b) => a.name.localeCompare(b.name))
                .forEach((b) => options.push({ value: b.nr.toString(), label: b.name }));
        }

        return options;
    }, [auth, betreuer]);

    const [filterValues, debouncedFilterValues, setFilterValues] = useDebouncedState<FilterValues>(() => {
        return {
            ...defaultFilterValues,
            betreuer: {
                value: auth.nr?.toString() || '',
                label: auth.name || '?',
            },
        };
    }, 1000);

    const filter = React.useMemo<BetreuerWorkMonthFilter>(
        () => ({
            betrNr: parseInt(debouncedFilterValues.betreuer.value, 10),
            workYear: debouncedFilterValues.workYear,
            workMonth: debouncedFilterValues.workMonth,
        }),
        [debouncedFilterValues],
    );

    const betreuerWorkMonthList = useBetreuerWorkMonthList(filter);

    const selectedYearOption = React.useMemo<SelectOption>(
        () => ({ value: filterValues.workYear.toString(), label: filterValues.workYear.toString() }),
        [filterValues.workYear],
    );

    const selectedMonthOption = React.useMemo<SelectOption>(
        () => ({ value: filterValues.workMonth.toString(), label: filterValues.workMonth.toString() }),
        [filterValues.workMonth],
    );

    const renderActionButtons = () => {
        if (betreuerWorkMonthList.isLoading || betreuerWorkMonthList.isFetching || !betreuerWorkMonthList.data) {
            return <div />;
        }

        if (betreuerWorkMonthList.data.length === 0) {
            return (
                <PrimaryButton
                    onClick={() => {
                        setShowInit(true);
                    }}
                    text="Starten"
                />
            );
        }

        return (
            <>
                <DefaultButton
                    className={formStyles.migrateButton}
                    disabled={betreuerWorkMonthList.data[0].isFinished}
                    onClick={() => setShowFinish(true)}
                    text="Abschließen"
                />

                <PrimaryButton
                    onClick={() => {
                        window.open(
                            generateReportPrintUrl('arbeitszeit', {
                                betrNr: parseInt(filterValues.betreuer.value, 10),
                                workYear: filterValues.workYear,
                                workMonth: filterValues.workMonth,
                            }),
                        );
                    }}
                    text="Drucken"
                />
            </>
        );
    };

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <Fabric className={styles.container}>
            <Helmet>
                <title>Arbeitszeit</title>
            </Helmet>
            <div className={styles.searchBar}>
                <div>
                    <Label>Betreuer</Label>
                    <Select
                        name="betreuer"
                        value={filterValues.betreuer}
                        options={betreuerFilterOptions}
                        onChange={(newValue: ValueType<SelectOption, false>): void => {
                            if (newValue) {
                                setFilterValues({
                                    ...filterValues,
                                    betreuer: newValue,
                                });
                            }
                        }}
                    />
                </div>

                <div>
                    <Label>Jahr</Label>
                    <Select
                        name="Jahr"
                        options={yearOptions}
                        value={selectedYearOption}
                        onChange={(newOption) => {
                            setFilterValues({
                                ...filterValues,
                                workYear: parseInt(newOption ? newOption.value : moment().year().toString(), 10),
                            });
                        }}
                    />
                </div>
                <div>
                    <Label>Monat</Label>
                    <Select
                        name="Monat"
                        options={monthOptions}
                        value={selectedMonthOption}
                        onChange={(newOption) => {
                            setFilterValues({
                                ...filterValues,
                                workMonth: parseInt(
                                    newOption ? newOption.value : (moment().month() + 1).toString(),
                                    10,
                                ),
                            });
                        }}
                    />
                </div>

                <div />

                <div className={styles.actionButtons}>{renderActionButtons()}</div>
            </div>

            <Arbeitszeit
                betrNr={parseInt(debouncedFilterValues.betreuer.value, 10)}
                workYear={debouncedFilterValues.workYear}
                workMonth={debouncedFilterValues.workMonth}
            />

            {showInit && (
                <ConfirmMessage
                    title="Monat starten?"
                    content={
                        <>
                            Das Vormonat muss abgeschlossen sein, Urlaub und Überstunden werden vom Vormonat übernommen.
                        </>
                    }
                    onConfirm={async () => {
                        try {
                            await initBetreuerWorkMonth(
                                parseInt(filterValues.betreuer.value, 10),
                                filterValues.workYear,
                                filterValues.workMonth,
                            );

                            betreuerWorkMonthList.refetch().then();
                        } catch (e) {
                            // eslint-disable-next-line no-alert
                            alert(e.message);
                        }

                        setShowInit(false);
                    }}
                    onCancel={() => setShowInit(false)}
                />
            )}

            {showFinish && (
                <ConfirmMessage
                    title="Monat abschließen?"
                    message="Danach können keine weiteren Änderungen mehr vorgenommen werden."
                    onConfirm={async () => {
                        try {
                            await finishBetreuerWorkMonth(
                                parseInt(filterValues.betreuer.value, 10),
                                filterValues.workYear,
                                filterValues.workMonth,
                            );
                            betreuerWorkMonthList.refetch().then();
                        } catch (e) {
                            // eslint-disable-next-line no-alert
                            alert(e.message);
                        }
                        setShowFinish(false);
                    }}
                    onCancel={() => setShowFinish(false)}
                />
            )}
        </Fabric>
    );
};
